import { SITE_CONFIG } from '../../../config/site-config';
import { logger } from '../../../lib/logger';
import { trackEvent } from '../../analytics/analytics';
import { FormDeliveryError, type DeliveryRequest, type FormPayload } from './delivery-types';
import { openEmailFallback } from './email-fallback';
import { selectProvider } from './providers';

export type FormFields = Readonly<Partial<Record<string, string>>>;

export function buildPayload(fields: FormFields, formType: string): FormPayload {
  return { form_type: formType, page_url: location.href, ...fields };
}

function buildRequest(fields: FormFields, formType: string): DeliveryRequest {
  const who = fields.company || fields.organization || fields.fullName || fields.name || '';
  return {
    payload: buildPayload(fields, formType),
    formType,
    subject: `[ReTax360] ${formType} — ${who}`,
    replyTo: fields.email ?? '',
  };
}

/**
 * Delivers a form submission through the configured provider. Resolves only when delivery succeeded
 * (and records `generate_lead`); rejects with FormDeliveryError otherwise.
 */
export async function sendForm(fields: FormFields, formType: string): Promise<void> {
  const request = buildRequest(fields, formType);
  const provider = selectProvider(SITE_CONFIG, ({ payload }) => {
    openEmailFallback(payload, formType);
  });
  try {
    await provider.send(request);
  } catch (error) {
    logger.error(`Form delivery through ${provider.name} failed.`, error);
    throw error instanceof FormDeliveryError ? error : new FormDeliveryError(provider.name, 'network error');
  }
  trackEvent('generate_lead', {
    form_type: formType,
    cta_source: request.payload.cta_source ?? '',
    page_source: request.payload.page_source ?? '',
  });
}
